import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface Alert {
  id: number;
  target_price: number;
  is_active: boolean;
  created_at: string;
  product: {
    id: number;
    title: string; // Veritabanında 'name' mi 'title' mı kontrol et, genelde title olur
    brand: string;
    current_price: number;
    image_url: string;
  };
}

export default function PriceAlerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  // VERİ ÇEKME FONKSİYONU (Tek ve Doğru Olan)
  const fetchMyAlerts = async () => {
    try {
      setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("price_alerts")
        .select(
          `
          id,
          target_price,
          is_active,
          created_at,
          product:products (
            id,
            title,
            brand,
            current_price,
            image_url
          )
        `,
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (data) {
        // Gelen veriyi Alert arayüzüne uygun hale getiriyoruz
        const formatted = data
          .map((item: any) => ({
            id: item.id,
            target_price: item.target_price,
            is_active: item.is_active,
            created_at: item.created_at,
            product: item.product, // products tablosundan gelen veri
          }))
          .filter((item) => item.product !== null); // Ürünü silinmiş alarmları gösterme

        setAlerts(formatted);
      }
    } catch (error) {
      console.error("Alarmlar yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyAlerts();

    // Diğer sayfalardan (ProductCard gibi) bir değişiklik gelirse listeyi tazele
    const handleUpdate = () => fetchMyAlerts();
    window.addEventListener("countsUpdated", handleUpdate);
    return () => window.removeEventListener("countsUpdated", handleUpdate);
  }, []);

  const toggleAlert = async (alertId: number, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("price_alerts")
        .update({ is_active: !currentStatus })
        .eq("id", alertId);

      if (error) throw error;

      // Arayüzü anında güncelle
      setAlerts((prev) =>
        prev.map((a) =>
          a.id === alertId ? { ...a, is_active: !currentStatus } : a,
        ),
      );
    } catch (error) {
      console.error("Hata:", error);
    }
  };

  const deleteAlert = async (alertId: number) => {
    try {
      const { error } = await supabase
        .from("price_alerts")
        .delete()
        .eq("id", alertId);

      if (error) throw error;

      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      window.dispatchEvent(new Event("countsUpdated")); // Navbar'daki sayıyı düşür
    } catch (error) {
      console.error("Hata:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="bg-white rounded-3xl p-16 border border-gray-100 text-center shadow-sm">
        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <i className="ri-notification-line text-4xl text-gray-300"></i>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Henüz Alarmınız Yok
        </h3>
        <p className="text-gray-500 mb-8 max-w-xs mx-auto text-sm">
          Takip ettiğiniz ürünlerin fiyatı düştüğünde size haber vereceğiz.
        </p>
        <button
          onClick={() => navigate("/")}
          className="px-8 py-3 bg-gray-900 text-white rounded-2xl font-bold hover:bg-rose-600 transition-all cursor-pointer"
        >
          Fırsatları Keşfet
        </button>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-md transition-all flex items-center gap-6 group relative"
        >
          {/* Ürün Resmi */}
          <div
            onClick={() => navigate(`/product/${alert.product?.id}`)}
            className="w-24 h-24 bg-gray-50 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer p-2"
          >
            <img
              src={alert.product?.image_url}
              alt=""
              className="w-full h-full object-contain mix-blend-multiply transition-transform group-hover:scale-105"
            />
          </div>

          {/* Bilgiler */}
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 line-clamp-1 text-lg leading-tight mb-2">
              {alert.product?.title}
            </h3>
            <div className="flex items-center gap-6">
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Mevcut
                </span>
                <span className="text-gray-900 font-bold">
                  {alert.product?.current_price?.toLocaleString("tr-TR")}₺
                </span>
              </div>
              <div className="w-px h-8 bg-gray-100"></div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-emerald-500">
                  Hedef
                </span>
                <span className="text-emerald-600 font-black">
                  {alert.target_price?.toLocaleString("tr-TR")}₺
                </span>
              </div>
            </div>
          </div>

          {/* Aksiyonlar */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => toggleAlert(alert.id, alert.is_active)}
              className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${alert.is_active ? "bg-emerald-500" : "bg-gray-200"}`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${alert.is_active ? "translate-x-5" : "translate-x-0"}`}
              ></span>
            </button>
            <button
              onClick={() => deleteAlert(alert.id)}
              className="w-10 h-10 flex items-center justify-center rounded-xl text-gray-300 hover:text-rose-500 hover:bg-rose-50 transition-all cursor-pointer"
            >
              <i className="ri-delete-bin-line text-xl"></i>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
